import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Sparkles, Plus, Link2, Loader2, Trash2, ChevronDown,
  Target, Zap, FileText, ArrowRight, Filter, Search,
  Paperclip, X, Upload
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  'Comparison': 'bg-blue-100 text-blue-700',
  'Ranking / Top-N': 'bg-purple-100 text-purple-700',
  'HowTo / Guide': 'bg-green-100 text-green-700',
  'FAQ / Q&A': 'bg-amber-100 text-amber-700',
  'Product Review': 'bg-cyan-100 text-cyan-700',
  'Case Study': 'bg-rose-100 text-rose-700',
  'Definition': 'bg-slate-100 text-slate-700',
};
// Dropdown options — English values sent to backend and stored in MongoDB
const TOPIC_TYPES = ['Comparison', 'Ranking / Top-N', 'HowTo / Guide', 'FAQ / Q&A', 'Product Review', 'Case Study', 'Definition'];
// Display normalization for values stored in MongoDB (English only, no Russian needed)
const FORMAT_DISPLAY = {
  'Comparison': 'Comparison', 'Ranking / Top-N': 'Ranking / Top-N',
  'HowTo / Guide': 'HowTo / Guide', 'HowTo': 'HowTo', 'FAQ': 'FAQ', 'FAQ / Q&A': 'FAQ / Q&A',
  'Product Review': 'Product Review', 'Case Study': 'Case Study',
  'Definition': 'Definition', 'Guide': 'Guide',
  'high': 'High', 'medium': 'Medium', 'low': 'Low',
  'High': 'High', 'Medium': 'Medium', 'Low': 'Low',
  'idea': 'Idea', 'in_progress': 'In progress', 'done': 'Done', 'published': 'Published',
  'Idea': 'Idea', 'In progress': 'In progress', 'Done': 'Done', 'Published': 'Published',
  'Draft': 'Draft', 'Generating...': 'Generating...', 'Error': 'Error',
};
const translateFormat = (val) => FORMAT_DISPLAY[val] || val;
const PRIORITY_COLORS = { 'high': 'text-red-600', 'medium': 'text-amber-600', 'low': 'text-slate-500', 'High': 'text-red-600', 'Medium': 'text-amber-600', 'Low': 'text-slate-500' };
const STATUS_COLORS = { 'idea': 'bg-slate-100 text-slate-600', 'Idea': 'bg-slate-100 text-slate-600', 'in_progress': 'bg-blue-100 text-blue-700', 'In progress': 'bg-blue-100 text-blue-700', 'done': 'bg-green-100 text-green-700', 'Done': 'bg-green-100 text-green-700', 'published': 'bg-purple-100 text-purple-700', 'Published': 'bg-purple-100 text-purple-700' };
// Priority helper: matches English values from backend
const isHigh = (p) => (p || '').toLowerCase() === 'high';
const isMedium = (p) => (p || '').toLowerCase() === 'medium';

const AnalysisTopics = ({ monitorId, monitor, geoId, geoLabel, geoLanguage }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('HowTo / Guide');
  const [pendingFiles, setPendingFiles] = useState([]);  // SESSION 91: files to upload
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [newPublication, setNewPublication] = useState(null);  // SESSION 92: {name, url}
  const [pubSearch, setPubSearch] = useState('');  // SESSION 92
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const { t } = useTranslation();

  useEffect(() => { loadTopics(); loadCachedAnalysis(); }, [monitorId]);

  // --- Resume operation state on page return ---
  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);
  useEffect(() => {
    const checkActive = async () => {
      try {
        const { data } = await axios.get(`${API}/api/promo/${monitorId}/operation-status`, { withCredentials: true });
        if (!mountedRef.current) return;
        if (data.active_operation === 'analyze_sources') {
          setAnalyzing(true);
          pollUntilDone('analyze_sources');
        } else if (data.active_operation === 'generate_topics') {
          setGenerating(true);
          pollUntilDone('generate_topics');
        }
      } catch (err) { /* ignore */ }
    };
    checkActive();
  }, [monitorId]); // eslint-disable-line react-hooks/exhaustive-deps
  const pollUntilDone = async (operation) => {
    const maxWait = 300;
    let elapsed = 0;
    while (elapsed < maxWait) {
      await new Promise(r => setTimeout(r, 3000));
      elapsed += 3;
      if (!mountedRef.current) return;
      try {
        const { data } = await axios.get(`${API}/api/promo/${monitorId}/operation-status`, { withCredentials: true });
        if (!mountedRef.current) return;
        if (data.active_operation !== operation) {
          if (operation === 'analyze_sources') { await loadCachedAnalysis(); setAnalyzing(false); }
          if (operation === 'generate_topics') { await loadTopics(); setGenerating(false); }
          return;
        }
      } catch (err) { /* continue polling */ }
    }
    setAnalyzing(false);
    setGenerating(false);
  };

  const loadCachedAnalysis = async () => {
    try {
      const { data } = await axios.get(`${API}/api/promo/${monitorId}/source-analysis`, { withCredentials: true });
      if (data && (data.recommended_platforms || data.source_analysis)) {
        setAnalysis(data);
      }
    } catch (err) { /* no cached analysis yet */ }
  };

  const loadTopics = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      const { data } = await axios.get(`${API}/api/promo/${monitorId}/topics?${params}`, { withCredentials: true });
      setTopics(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!loading) loadTopics(); }, [filterStatus, filterPriority]);

  const handleAnalyzeSources = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/api/promo/${monitorId}/analyze-sources`, {}, { withCredentials: true });
      setAnalysis(data);
      setAnalyzing(false);
    } catch (err) {
      if (err.response?.status === 409) { pollUntilDone('analyze_sources'); return; }
      alert(err.response?.data?.detail || t('topics.ошибка_анализа'));
      setAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/promo/${monitorId}/generate-topics`, { count: 10, ...(geoId && { geo_id: geoId }) }, { withCredentials: true });
      setTopics(prev => [...data.topics, ...prev]);
      setGenerating(false);
    } catch (err) {
      if (err.response?.status === 409) { pollUntilDone('generate_topics'); return; }
      alert(err.response?.data?.detail || t('topics.ошибка_генерации'));
      setGenerating(false);
    }
  };

  const handleFromUrl = async () => {
    if (!urlInput.trim()) return;
    setUrlLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/promo/${monitorId}/topics/from-url`, { url: urlInput.trim() }, { withCredentials: true });
      setTopics(prev => [...data.topics, ...prev]);
      setUrlInput('');
    } catch (err) {
      alert(err.response?.data?.detail || t('topics.ошибка'));
    } finally { setUrlLoading(false); }
  };

  const handleAddManual = async () => {
    if (!newTitle.trim()) return;
    try {
      const { data: topicData } = await axios.post(`${API}/api/promo/${monitorId}/topics`, {
        title: newTitle.trim(), type: newType,
        target_platforms: ['ChatGPT', 'Perplexity', 'Google AI', 'Grok', 'Google AI Overviews', 'Google AI Mode', 'Bing Copilot', 'You.com'],
        priority: 'Medium',
        ...(newPublication ? { target_publication: newPublication } : {}),  // SESSION 92
      }, { withCredentials: true });

      // SESSION 91: Upload attached files if any
      if (pendingFiles.length > 0) {
        setUploadingFiles(true);
        try {
          const formData = new FormData();
          pendingFiles.forEach(f => formData.append('files', f));
          const { data: attachResp } = await axios.post(
            `${API}/api/promo/${monitorId}/topics/${topicData.id}/attachments`,
            formData,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          topicData.attachments = attachResp.attachments;
        } catch (uploadErr) {
          console.error('File upload failed:', uploadErr);
          alert(`Topic created, but file upload failed: ${uploadErr.response?.data?.detail || uploadErr.message}`);
        } finally {
          setUploadingFiles(false);
        }
      }

      setTopics(prev => [topicData, ...prev]);
      setNewTitle(''); setPendingFiles([]); setNewPublication(null); setPubSearch(''); setShowAddForm(false);
    } catch (err) { alert(err.response?.data?.detail || t('topics.ошибка')); }
  };

  // SESSION 91: File handling
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const total = pendingFiles.length + selected.length;
    if (total > 10) { alert('Maximum 10 files'); return; }
    const tooBig = selected.find(f => f.size > 5 * 1024 * 1024);
    if (tooBig) { alert(`File "${tooBig.name}" exceeds 5MB limit`); return; }
    setPendingFiles(prev => [...prev, ...selected]);
    e.target.value = '';  // reset input
  };

  const handleRemovePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteAttachment = async (topicId, attachmentId) => {
    try {
      const { data: resp } = await axios.delete(
        `${API}/api/promo/${monitorId}/topics/${topicId}/attachments/${attachmentId}`,
        { withCredentials: true }
      );
      setTopics(prev => prev.map(t => t.id === topicId ? { ...t, attachments: resp.attachments } : t));
    } catch (err) { console.error(err); }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('topics.удалить_тему'))) return;
    try {
      await axios.delete(`${API}/api/promo/${monitorId}/topics/${id}`, { withCredentials: true });
      setTopics(prev => prev.filter(t => t.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleWriteArticle = async (topicId) => {
    setTopics(prev => prev.map(t => t.id === topicId ? { ...t, _generating: true } : t));
    try {
      const { data } = await axios.post(`${API}/api/promo/${monitorId}/articles/generate`, { topic_id: topicId }, { withCredentials: true });
      // Article generation is async - just update the topic status
      setTopics(prev => prev.map(t => t.id === topicId ? { ...t, status: 'In progress', _generating: false } : t));
      alert(t('topics.статья_генерируется', { id: data.id }));
    } catch (err) {
      alert(err.response?.data?.detail || t('topics.ошибка_генерации_статьи'));
      setTopics(prev => prev.map(t => t.id === topicId ? { ...t, _generating: false } : t));
    }
  };

  return (
    <div className="space-y-6">
      {/* M1 Analysis Section */}
      <div className="bg-white border border-[#E2DDD8] p-5" data-testid="m1-analysis-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-800">{t('topics.анализ_источников')}</h3>
          <button onClick={handleAnalyzeSources} disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#159A7F] disabled:opacity-50 transition-colors"
            data-testid="analyze-sources-btn">
            {analyzing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('topics.анализ_загрузка')}</> : <><Search className="w-3.5 h-3.5" /> {t('topics.анализировать')}</>}
          </button>
        </div>
        {analysis && (
          <div className="space-y-4 mt-4" data-testid="analysis-results">
            {analysis.content_platforms?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">{t('topics.площадки_статей')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.content_platforms.map((p, i) => (
                    <div key={i} className={`border-l-4 rounded p-4 bg-white border border-[#E2DDD8] ${
                      isHigh(p.priority) ? 'border-l-[#1BB594]' :
                      isMedium(p.priority) ? 'border-l-amber-500' : 'border-l-slate-300'
                    }`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-bold text-slate-800">{p.platform}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                          isHigh(p.priority) ? 'bg-red-100 text-red-700' :
                          isMedium(p.priority) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                        }`}>{translateFormat(p.priority)}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-2">{p.action || p.reason}</p>
                      {p.content_types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {p.content_types.map((ct, j) => (
                            <span key={j} className="text-[10px] bg-[#1BB594]/10 text-[#1BB594] rounded px-1.5 py-0.5">{translateFormat(ct)}</span>
                          ))}
                        </div>
                      )}
                      {p.priority_engines?.length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1">{t('topics.движки')} {p.priority_engines.join(', ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.review_platforms?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">{t('topics.площадки_отзывов')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.review_platforms.map((p, i) => (
                    <div key={i} className={`border-l-4 rounded p-4 bg-white border border-[#E2DDD8] ${
                      isHigh(p.priority) ? 'border-l-blue-500' :
                      isMedium(p.priority) ? 'border-l-amber-500' : 'border-l-slate-300'
                    }`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-bold text-slate-800">{p.platform}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                          isHigh(p.priority) ? 'bg-red-100 text-red-700' :
                          isMedium(p.priority) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                        }`}>{translateFormat(p.priority)}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-2">{p.action || p.reason}</p>
                      {p.priority_engines?.length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1">{t('topics.движки')} {p.priority_engines.join(', ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.marketplace_platforms?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">{t('topics.маркетплейсы')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.marketplace_platforms.map((p, i) => (
                    <div key={i} className={`border-l-4 rounded p-4 bg-white border border-[#E2DDD8] ${
                      isHigh(p.priority) ? 'border-l-purple-500' :
                      isMedium(p.priority) ? 'border-l-amber-500' : 'border-l-slate-300'
                    }`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-bold text-slate-800">{p.platform}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                          isHigh(p.priority) ? 'bg-red-100 text-red-700' :
                          isMedium(p.priority) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                        }`}>{translateFormat(p.priority)}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-2">{p.action || p.reason}</p>
                      {p.priority_engines?.length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1">{t('topics.движки')} {p.priority_engines.join(', ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!analysis.content_platforms?.length && !analysis.review_platforms?.length && analysis.recommended_platforms?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">{t('topics.где_публиковать')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.recommended_platforms.map((p, i) => (
                    <div key={i} className={`border-l-4 rounded p-4 bg-white border border-[#E2DDD8] ${
                      isHigh(p.priority) ? 'border-l-red-500' :
                      isMedium(p.priority) ? 'border-l-amber-500' : 'border-l-slate-300'
                    }`} data-testid={`platform-rec-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-bold text-slate-800">{p.platform}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                          isHigh(p.priority) ? 'bg-red-100 text-red-700' :
                          isMedium(p.priority) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                        }`}>{translateFormat(p.priority)}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{p.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.missing_platforms?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">{t('topics.рекомендуемые_площадки')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysis.missing_platforms.map((p, i) => (
                    <div key={i} className="border-l-4 border-l-orange-400 border border-dashed border-orange-200 rounded p-4 bg-orange-50/30">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm font-bold text-slate-800">{p.platform}</p>
                        <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase bg-orange-100 text-orange-700">{t('topics.нужно')}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-1">{p.action || p.reason || p.why_needed}</p>
                      {p.priority_engines?.length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-1">{t('topics.движки')} {p.priority_engines.join(', ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.source_map?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{t('topics.карта_источников')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {analysis.source_map.map((s, i) => (
                    <div key={i} className="border border-[#E2DDD8] rounded p-3" data-testid={`source-map-${i}`}>
                      <p className="text-xs font-bold text-slate-800 mb-1">{s.engine}</p>
                      <p className="text-[10px] text-slate-500">{t('topics.типы')} {(s.resource_types || []).map(translateFormat).join(', ')}</p>
                      <p className="text-[10px] text-[#1BB594] mt-0.5">{t('topics.домены')} {(s.top_domains || []).join(', ')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {analysis.blind_spots?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{t('topics.белые_пятна')}</h4>
                <div className="flex flex-wrap gap-2">
                  {analysis.blind_spots.slice(0, 8).map((bs, i) => (
                    <span key={i} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded px-2.5 py-1">
                      {bs.query || JSON.stringify(bs)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {analysis.top_formats?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{t('topics.топ_форматы')}</h4>
                <div className="flex gap-2">
                  {analysis.top_formats.map((f, i) => (
                    <span key={i} className="text-xs bg-[#1BB594]/5 text-[#1BB594] rounded px-2.5 py-1 font-medium">{translateFormat(f)}</span>
                  ))}
                </div>
              </div>
            )}
            {analysis.competitor_content_map?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{t('topics.контент_конкурентов')}</h4>
                <div className="space-y-1.5">
                  {analysis.competitor_content_map.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs text-slate-600 border border-[#E2DDD8] rounded px-3 py-2" data-testid={`competitor-content-${i}`}>
                      <span className="font-bold text-slate-800">{c.brand}</span>
                      <span className="text-slate-400">|</span>
                      <span>{(c.content_types || []).map(translateFormat).join(', ')}</span>
                      <span className="text-slate-400">|</span>
                      <span className="text-[#1BB594]">{(c.platforms || []).join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* M2 Topics Section */}
      <div className="bg-white border border-[#E2DDD8] p-5" data-testid="m2-topics-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-800">{t('topics.темы_статей', { count: topics.length })}</h3>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-1.5 px-3 py-2 border border-[#E2DDD8] text-xs font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
              data-testid="add-topic-btn">
              <Plus className="w-3.5 h-3.5" /> {t('topics.добавить_тему')}
            </button>
            <button onClick={handleGenerate} disabled={generating}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#159A7F] disabled:opacity-50"
              data-testid="generate-topics-btn">
              {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('topics.генерация_загрузка')}</> : <><Sparkles className="w-3.5 h-3.5" /> {t('topics.генерировать_темы')}</>}
            </button>
          </div>
        </div>

        {/* URL input */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 flex gap-2">
            <input value={urlInput} onChange={e => setUrlInput(e.target.value)}
              placeholder={t('topics.url_placeholder')}
              className="flex-1 border border-[#E2DDD8] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#1BB594]"
              data-testid="url-input" />
            <button onClick={handleFromUrl} disabled={urlLoading || !urlInput.trim()}
              className="flex items-center gap-1.5 px-3 py-2 border border-[#1BB594]/30 text-[#1BB594] text-xs font-bold uppercase hover:bg-[#1BB594] hover:text-white disabled:opacity-40 transition-colors"
              data-testid="url-submit-btn">
              {urlLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              {t('topics.из_url')}
            </button>
          </div>
        </div>

        {/* Manual add form */}
        {showAddForm && (
          <div className="border border-[#E2DDD8] rounded p-4 mb-4 bg-slate-50" data-testid="add-topic-form">
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
              placeholder={t('topics.заголовок_placeholder')}
              className="w-full border border-[#E2DDD8] rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:border-[#1BB594]"
              data-testid="new-topic-title" />

            {/* SESSION 91: File attachments */}
            <div className="mb-3">
              <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-[#E2DDD8] rounded cursor-pointer hover:border-[#1BB594] hover:bg-[#1BB594]/5 transition-colors">
                <Upload className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500">
                  {pendingFiles.length > 0 ? `${pendingFiles.length}/10 files` : 'Attach reference files (PDF, DOCX, TXT, CSV... up to 10, 5MB each)'}
                </span>
                <input type="file" multiple className="hidden"
                  accept=".txt,.md,.csv,.json,.html,.htm,.pdf,.docx,.xlsx,.doc,.rtf,.xml,.yaml,.yml"
                  onChange={handleFileSelect} data-testid="file-input" />
              </label>
              {pendingFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {pendingFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 bg-white border border-[#E2DDD8] rounded text-xs">
                      <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-700 truncate flex-1">{f.name}</span>
                      <span className="text-slate-400 flex-shrink-0">{formatFileSize(f.size)}</span>
                      <button onClick={() => handleRemovePendingFile(i)} className="text-slate-300 hover:text-red-500 flex-shrink-0">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* SESSION 92: Target publication selector */}
            <div className="relative mb-3">
              <input
                value={newPublication ? newPublication.name : pubSearch}
                onChange={e => { setPubSearch(e.target.value); if (newPublication) setNewPublication(null); }}
                placeholder="Target publication (optional, e.g. Search Engine Land)"
                className="w-full border border-[#E2DDD8] rounded px-3 py-2 text-sm focus:outline-none focus:border-[#1BB594]"
                data-testid="target-publication-input" />
              {pubSearch && !newPublication && (() => {
                const allPlatforms = [
                  ...(analysis?.content_platforms || []),
                  ...(analysis?.review_platforms || []),
                  ...(analysis?.marketplace_platforms || []),
                  ...(analysis?.missing_platforms || []),
                  ...(analysis?.recommended_platforms || []),
                ];
                const seen = new Set();
                const unique = allPlatforms.filter(p => {
                  const n = p.platform || p.name || '';
                  if (seen.has(n) || !n) return false;
                  seen.add(n); return true;
                });
                const filtered = unique.filter(p =>
                  (p.platform || p.name || '').toLowerCase().includes(pubSearch.toLowerCase())
                ).slice(0, 8);
                return (filtered.length > 0 || pubSearch.length > 2) ? (
                  <div className="absolute z-10 w-full bg-white border border-[#E2DDD8] rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                    {filtered.map((p, i) => (
                      <div key={i}
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm flex justify-between"
                        onClick={() => { setNewPublication({ name: p.platform || p.name, url: '' }); setPubSearch(''); }}>
                        <span>{p.platform || p.name}</span>
                        {p.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          p.priority === 'high' ? 'bg-red-100 text-red-600' :
                          p.priority === 'medium' ? 'bg-amber-100 text-amber-600' :
                          'bg-slate-100 text-slate-500'
                        }`}>{p.priority}</span>}
                      </div>
                    ))}
                    {pubSearch.length > 2 && (
                      <div className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-500 border-t"
                        onClick={() => { setNewPublication({ name: pubSearch, url: '' }); setPubSearch(''); }}>
                        Use "{pubSearch}" as custom publication
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
              {newPublication && (
                <button onClick={() => { setNewPublication(null); setPubSearch(''); }}
                  className="absolute right-2 top-2 text-slate-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex gap-2 items-center">
              <select value={newType} onChange={e => setNewType(e.target.value)}
                className="border border-[#E2DDD8] rounded px-3 py-2 text-sm"
                data-testid="new-topic-type">
                {TOPIC_TYPES.map(type => <option key={type} value={type}>{translateFormat(type)}</option>)}
              </select>
              <button onClick={handleAddManual} disabled={uploadingFiles}
                className="px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase hover:bg-[#159A7F] disabled:opacity-50"
                data-testid="save-topic-btn">
                {uploadingFiles ? <><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Uploading...</> : t('topics.сохранить')}
              </button>
              <button onClick={() => { setShowAddForm(false); setPendingFiles([]); setNewPublication(null); setPubSearch(''); }} className="px-3 py-2 text-xs text-slate-500 hover:text-slate-800">{t('topics.отмена')}</button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border border-[#E2DDD8] rounded px-2 py-1.5 text-xs" data-testid="filter-status">
            <option value="">All statuses</option>
            <option value="Idea">Idea</option>
            <option value="In progress">In progress</option>
            <option value="Done">Done</option>
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
            className="border border-[#E2DDD8] rounded px-2 py-1.5 text-xs" data-testid="filter-priority">
            <option value="">All priorities</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>

        {/* Topics list */}
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 text-[#1BB594] animate-spin mx-auto" /></div>
        ) : topics.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
            <p className="text-sm">{t('topics.нет_тем')}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="topics-list">
            {topics.map(topic => (
              <div key={topic.id} className="border border-[#E2DDD8] rounded p-4 hover:border-[#1BB594]/30 transition-colors group" data-testid={`topic-${topic.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800 mb-1.5">{topic.title}</p>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${TYPE_COLORS[topic.type] || 'bg-slate-100 text-slate-600'}`}>{translateFormat(topic.type)}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${STATUS_COLORS[topic.status] || ''}`}>{translateFormat(topic.status)}</span>
                      <span className={`text-[10px] font-bold ${PRIORITY_COLORS[topic.priority] || ''}`}>{translateFormat(topic.priority)}</span>
                      {topic.keywords?.slice(0, 2).map((kw, i) => (
                        <span key={i} className="text-[10px] bg-slate-50 text-slate-500 rounded px-1.5 py-0.5">{kw}</span>
                      ))}
                      {topic.target_publication?.name && (
                        <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-bold">→ {topic.target_publication.name}</span>
                      )}
                      {topic.attachments?.length > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-blue-500 bg-blue-50 rounded px-1.5 py-0.5">
                          <Paperclip className="w-2.5 h-2.5" /> {topic.attachments.length} file{topic.attachments.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {/* SESSION 91: Show attached files */}
                    {topic.attachments?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {topic.attachments.map((att) => (
                          <span key={att.id} className="inline-flex items-center gap-1 text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 group/att">
                            <Paperclip className="w-2.5 h-2.5 text-slate-400" />
                            <span className="text-slate-600 max-w-[120px] truncate">{att.filename}</span>
                            <span className="text-slate-300">{formatFileSize(att.size)}</span>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(topic.id, att.id); }}
                              className="text-slate-200 hover:text-red-500 opacity-0 group-hover/att:opacity-100 transition-opacity">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {(topic.status === 'Идея' || topic.status === 'idea' || topic.status === 'Idea') && (
                      <button onClick={() => handleWriteArticle(topic.id)} disabled={topic._generating}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#1BB594] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#159A7F] disabled:opacity-50"
                        data-testid={`write-article-${topic.id}`}>
                        {topic._generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                        {t('topics.написать')}
                      </button>
                    )}
                    <button onClick={() => handleDelete(topic.id)}
                      className="p-1.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-topic-${topic.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisTopics;
